// Code generated by master-scheme ServiceTypesGenerator - DO NOT EDIT
// Generator: /Users/victorvins/Projects/master-scheme/dsl/src/ts-api/ServiceTypesGenerator.scala

import { postEndpoint } from '@innolab-trader/tradovate-transport'
import type { ValidationError } from '@innolab-trader/tradovate-types'
import type { SignUpResult } from '@innolab-trader/tradovate-types/src/enums/signUpResult'
import type { SocialSignInProvider } from '@innolab-trader/tradovate-types/src/enums/socialSignInProvider'

export interface SocialSignUp {
  provider: SocialSignInProvider
  token: string
  email: string
  firstName?: string
  lastName?: string
  organization?: string
  tac?: string
  appId?: string
  locale?: string
  source?: string
  promo?: string
  fib?: string
  introducingPartner?: string
  experience?: string
  heapUserId?: string
  originOrganization?: string
}

export interface SignUpResponse {
  errorText?: string
  errorCode: SignUpResult
  userId?: number
  emailVerified: boolean
  promoText?: string
}

/**
 * Validate SocialSignUp request
 *
 * Follows Rust validation pattern with structured errors
 *
 * @param request - The request object to validate
 * @returns Array of validation errors, empty if valid
 *
 * @example
 * ```typescript
 * const errors = validateSocialSignUp(requestData)
 * if (errors.length > 0) {
 *   return { status: 400, errors }
 * }
 * ```
 */
export function validateSocialSignUp(request: SocialSignUp): ValidationError[] {
  const errors: ValidationError[] = []

  const req = request as unknown as Record<string, unknown>
  if (req.provider === undefined || req.provider === null) {
    errors.push({
      field: 'provider',
      code: 'required',
      message: 'provider is required'
    })
  }

  if (req.token === undefined || req.token === null) {
    errors.push({
      field: 'token',
      code: 'required',
      message: 'token is required'
    })
  }
  if (typeof req.token === 'string' && req.token.length > 8192) {
    errors.push({
      field: 'token',
      code: 'too_long',
      message: 'token must not exceed 8192 characters',
      argument: '8192'
    })
  }
  if (typeof req.token === 'string' && req.token.trim() === '') {
    errors.push({
      field: 'token',
      code: 'required',
      message: 'token cannot be empty'
    })
  }

  if (req.email === undefined || req.email === null) {
    errors.push({
      field: 'email',
      code: 'required',
      message: 'email is required'
    })
  }
  if (typeof req.email === 'string' && req.email.length > 64) {
    errors.push({
      field: 'email',
      code: 'too_long',
      message: 'email must not exceed 64 characters',
      argument: '64'
    })
  }
  if (typeof req.email === 'string' && req.email.trim() === '') {
    errors.push({
      field: 'email',
      code: 'required',
      message: 'email cannot be empty'
    })
  }

  if (typeof req.firstName === 'string' && req.firstName.length > 64) {
    errors.push({
      field: 'firstName',
      code: 'too_long',
      message: 'firstName must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.lastName === 'string' && req.lastName.length > 64) {
    errors.push({
      field: 'lastName',
      code: 'too_long',
      message: 'lastName must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.organization === 'string' && req.organization.length > 64) {
    errors.push({
      field: 'organization',
      code: 'too_long',
      message: 'organization must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.tac === 'string' && req.tac.length > 64) {
    errors.push({
      field: 'tac',
      code: 'too_long',
      message: 'tac must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.appId === 'string' && req.appId.length > 64) {
    errors.push({
      field: 'appId',
      code: 'too_long',
      message: 'appId must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.locale === 'string' && req.locale.length > 64) {
    errors.push({
      field: 'locale',
      code: 'too_long',
      message: 'locale must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.source === 'string' && req.source.length > 64) {
    errors.push({
      field: 'source',
      code: 'too_long',
      message: 'source must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.promo === 'string' && req.promo.length > 8192) {
    errors.push({
      field: 'promo',
      code: 'too_long',
      message: 'promo must not exceed 8192 characters',
      argument: '8192'
    })
  }

  if (typeof req.fib === 'string' && req.fib.length > 64) {
    errors.push({
      field: 'fib',
      code: 'too_long',
      message: 'fib must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.introducingPartner === 'string' && req.introducingPartner.length > 64) {
    errors.push({
      field: 'introducingPartner',
      code: 'too_long',
      message: 'introducingPartner must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.experience === 'string' && req.experience.length > 64) {
    errors.push({
      field: 'experience',
      code: 'too_long',
      message: 'experience must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.heapUserId === 'string' && req.heapUserId.length > 64) {
    errors.push({
      field: 'heapUserId',
      code: 'too_long',
      message: 'heapUserId must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.originOrganization === 'string' && req.originOrganization.length > 64) {
    errors.push({
      field: 'originOrganization',
      code: 'too_long',
      message: 'originOrganization must not exceed 64 characters',
      argument: '64'
    })
  }

  return errors
}

export const socialSignUp = postEndpoint<SocialSignUp, SignUpResponse>('/auth/socialSignUp')
